import { useCallback, useEffect, useRef, useState } from 'react';
import { createService, createSystem, updateService } from '../services/service-config.service';

const RESERVED_METADATA_KEYS = new Set(['cms-key', 'cms-document-id']);

const toMetadataPairs = (metadata) => {
  if (!metadata || typeof metadata !== 'object') {
    return [];
  }

  return Object.entries(metadata)
    .filter(([key]) => !RESERVED_METADATA_KEYS.has(key))
    .map(([key, value]) => ({ key, value: typeof value === 'string' ? value : JSON.stringify(value) }));
};

const buildMetadataObject = (pairs) =>
  pairs.reduce((acc, { key, value }) => {
    const trimmed = key.trim();
    return trimmed === '' ? acc : { ...acc, [trimmed]: value };
  }, {});

const serializePairs = (pairs) => JSON.stringify(buildMetadataObject(pairs));

const emptyForm = () => ({
  active: true,
  cmsKey: '',
  contractedLicenses: '',
  description: '',
  displayName: '',
  endDate: '',
  explorable: false,
  iconKey: '',
  mainUrl: '',
  metadataPairs: [],
  serviceName: '',
  startDate: '',
  systemKey: '',
});

const toForm = (service) =>
  service
    ? {
        active: Boolean(service.active),
        cmsKey: service.cmsKey || '',
        contractedLicenses: service.contractedLicenses == null ? '' : String(service.contractedLicenses),
        description: service.description || '',
        displayName: service.displayName || '',
        endDate: service.endDate || '',
        explorable: Boolean(service.explorable),
        iconKey: service.iconKey || '',
        mainUrl: service.mainUrl || '',
        metadataPairs: toMetadataPairs(service.metadata),
        serviceName: service.serviceName || '',
        startDate: service.startDate || '',
        systemKey: service.systemKey == null ? '' : String(service.systemKey),
      }
    : emptyForm();

const optionalStr = (key, value) => {
  const trimmed = value.trim();
  return trimmed === '' ? {} : { [key]: trimmed };
};

const buildCreateBody = (form) => ({
  active: form.active,
  cmsKey: form.cmsKey.trim(),
  displayName: form.displayName.trim(),
  explorable: form.explorable,
  serviceName: form.serviceName.trim(),
  systemKey: Number(form.systemKey),
  ...optionalStr('description', form.description),
  ...optionalStr('mainUrl', form.mainUrl),
  ...optionalStr('iconKey', form.iconKey),
  ...(form.contractedLicenses.trim() === '' ? {} : { contractedLicenses: Number(form.contractedLicenses) }),
  ...optionalStr('startDate', form.startDate),
  ...(form.active ? {} : optionalStr('endDate', form.endDate)),
  ...(form.metadataPairs.length === 0 ? {} : { metadata: buildMetadataObject(form.metadataPairs) }),
});

const patchTrimmedString = (body, key, formValue, initialValue, nullWhenEmpty = false) => {
  if (formValue.trim() === initialValue.trim()) {
    return;
  }

  body[key] = nullWhenEmpty ? formValue.trim() || null : formValue.trim();
};

const patchActiveAndEndDate = (body, form, initial) => {
  if (form.active === initial.active && form.endDate === initial.endDate) {
    return;
  }

  body.active = form.active;
  body.endDate = form.active || form.endDate === '' ? null : form.endDate;
};

const buildUpdateBody = (form, initial) => {
  const body = {};

  patchTrimmedString(body, 'displayName', form.displayName, initial.displayName);
  patchTrimmedString(body, 'description', form.description, initial.description, true);
  patchTrimmedString(body, 'mainUrl', form.mainUrl, initial.mainUrl, true);
  patchTrimmedString(body, 'iconKey', form.iconKey, initial.iconKey, true);
  patchTrimmedString(body, 'cmsKey', form.cmsKey, initial.cmsKey);

  if (form.contractedLicenses.trim() !== initial.contractedLicenses.trim()) {
    body.contractedLicenses = form.contractedLicenses.trim() === '' ? null : Number(form.contractedLicenses);
  }
  if (form.explorable !== initial.explorable) {
    body.explorable = form.explorable;
  }
  if (Number(form.systemKey) !== Number(initial.systemKey)) {
    body.systemKey = Number(form.systemKey);
  }
  if (form.startDate !== initial.startDate) {
    body.startDate = form.startDate === '' ? null : form.startDate;
  }

  patchActiveAndEndDate(body, form, initial);

  if (serializePairs(form.metadataPairs) !== serializePairs(initial.metadataPairs)) {
    body.metadata = buildMetadataObject(form.metadataPairs);
  }

  return body;
};

const validate = (form) => {
  if (form.serviceName.trim() === '') {
    return 'Service name is required.';
  }
  if (form.displayName.trim() === '') {
    return 'Display name is required.';
  }
  if (form.cmsKey.trim() === '') {
    return 'CMS key (SKU) is required.';
  }
  if (form.systemKey === '') {
    return 'System is required.';
  }
  return null;
};

const extractErrorMessage = async (err, fallback) => {
  try {
    if (err && typeof err.json === 'function') {
      const body = await err.json();
      if (body && typeof body.error === 'string') {
        return body.error;
      }
    }
  } catch (parseError) {
    console.error('ServiceConfiguration: failed to parse error response', parseError);
  }

  if (err && typeof err.status === 'number') {
    if (err.status === 409) {
      return 'A service with this name already exists.';
    }
    if (err.status === 502) {
      return 'The CMS could not be reached. The service was not created.';
    }
  }

  return fallback;
};

export const useServiceFormHook = ({ onSaved, onSystemCreated, open, service, systems }) => {
  const isEdit = Boolean(service);
  const initialRef = useRef(emptyForm());
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(/** @type {string | null} */ (null));
  const [addingSystem, setAddingSystem] = useState(false);
  const [newSystemName, setNewSystemName] = useState('');
  const [systemSaving, setSystemSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const next = toForm(service);
      initialRef.current = next;
      setForm(next);
      setFormError(null);
      setAddingSystem(false);
      setNewSystemName('');
    }
  }, [open, service]);

  const patch = useCallback((changes) => {
    setForm((prev) => ({ ...prev, ...changes }));
  }, []);

  const addMetadataPair = useCallback(() => {
    setForm((prev) => ({ ...prev, metadataPairs: [...prev.metadataPairs, { key: '', value: '' }] }));
  }, []);

  const patchMetadataPair = useCallback((index, changes) => {
    setForm((prev) => ({
      ...prev,
      metadataPairs: prev.metadataPairs.map((pair, i) => (i === index ? { ...pair, ...changes } : pair)),
    }));
  }, []);

  const removeMetadataPair = useCallback((index) => {
    setForm((prev) => ({ ...prev, metadataPairs: prev.metadataPairs.filter((_, i) => i !== index) }));
  }, []);

  const handleCreateSystem = useCallback(async () => {
    const name = newSystemName.trim();
    if (name === '') {
      return;
    }

    setSystemSaving(true);
    try {
      const created = await createSystem({ systemName: name });
      if (created && created.systemId != null) {
        setForm((prev) => ({ ...prev, systemKey: String(created.systemId) }));
        if (onSystemCreated) {
          onSystemCreated(created);
        }
      }
      setAddingSystem(false);
      setNewSystemName('');
    } catch (requestError) {
      console.error('ServiceConfiguration: failed to create system', requestError);
      setFormError('Failed to create the new system.');
    } finally {
      setSystemSaving(false);
    }
  }, [newSystemName, onSystemCreated]);

  const submit = useCallback(async () => {
    const validationError = validate(form);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      if (isEdit) {
        const body = buildUpdateBody(form, initialRef.current);
        if (Object.keys(body).length === 0) {
          setSaving(false);
          onSaved('No changes to save');
          return;
        }
        await updateService(service.serviceId, body);
        onSaved('Service updated');
      } else {
        await createService(buildCreateBody(form));
        onSaved('Service created');
      }
    } catch (requestError) {
      const message = await extractErrorMessage(requestError, isEdit ? 'Update failed' : 'Create failed');
      setFormError(message);
    } finally {
      setSaving(false);
    }
  }, [form, isEdit, onSaved, service]);

  return {
    addMetadataPair,
    addingSystem,
    form,
    formError,
    handleCreateSystem,
    isEdit,
    newSystemName,
    patch,
    patchMetadataPair,
    removeMetadataPair,
    saving,
    setAddingSystem,
    setNewSystemName,
    submit,
    systems,
    systemSaving,
  };
};
